import { useEffect, useRef, useState } from 'react';

const VARAKTIGHET_MS = 800;

/**
 * Senast visade värde per nyckel, så länge sidan lever. Insikters flikar
 * monteras om vid varje byte – utan minnet räknade alla kort upp från 0 igen,
 * vilket läste som att datan laddades om.
 */
const senast = new Map<string, number>();

function minskadRorelse(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Räknar upp ett nyckeltal till sitt värde – från 0 första gången kortet
 * visas, därefter från det värde som senast visades. Ger sidan liv utan att
 * hitta på något: siffran som landar är alltid den riktiga.
 *
 * Med prefers-reduced-motion visas värdet direkt.
 */
export function useRaknaUpp(mal: number, nyckel: string): number {
  const [visat, setVisat] = useState(() => (minskadRorelse() ? mal : (senast.get(nyckel) ?? 0)));
  const visatRef = useRef(visat);

  useEffect(() => {
    const satt = (v: number) => {
      visatRef.current = v;
      senast.set(nyckel, v);
      setVisat(v);
    };

    if (minskadRorelse()) {
      const id = requestAnimationFrame(() => satt(mal));
      return () => cancelAnimationFrame(id);
    }

    const fran = visatRef.current;
    if (fran === mal) {
      senast.set(nyckel, mal);
      return;
    }
    const start = performance.now();
    let id = 0;
    const steg = (nu: number) => {
      const t = Math.min(1, (nu - start) / VARAKTIGHET_MS);
      // ease-out-cubic: snabbt i början, mjuk inbromsning mot det riktiga värdet
      satt(Math.round(fran + (mal - fran) * (1 - (1 - t) ** 3)));
      if (t < 1) id = requestAnimationFrame(steg);
    };
    id = requestAnimationFrame(steg);
    return () => cancelAnimationFrame(id);
  }, [mal, nyckel]);

  return visat;
}
